import React from 'react';
import 'fragments/announcements/announcement-detail.scss';
import { Empty, Modal, Progress, Spin, Tag } from 'antd';
import { ArrowLeftOutlined, EyeOutlined, SoundOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import { useAnnouncementDetailController } from 'fragments/announcements/announcement-detail.controller';

const { confirm } = Modal;

export const AnnouncementDetailFragment: React.FC = () => {
    const { translate } = useTranslator();
    const { state, events } = useAnnouncementDetailController();

    const showConfirmRetire = () => {
        confirm({
            title: translate({ key: 'announcements.retire-confirm' }),
            okText: translate({ key: 'general.remove' }),
            cancelText: translate({ key: 'general.cancel' }),
            okButtonProps: { className: 'delete' },
            cancelButtonProps: { className: 'btn-cancel' },
            onOk: () => events.onRetire(),
        });
    };

    const header = (
        <div className={'announcement-detail-header'}>
            <button
                type={'button'}
                className={'announcement-detail-back'}
                aria-label={translate({ key: 'general.back' })}
                onClick={events.onBack}
            >
                <ArrowLeftOutlined aria-hidden />
            </button>
            <h1 className={'announcement-detail-header-title'}>{translate({ key: 'announcements.title' })}</h1>
        </div>
    );

    if (state.loading) {
        return (
            <div className={'announcement-detail'}>
                {header}
                <Spin className={'announcement-detail-spin'} />
            </div>
        );
    }

    if (!state.announcement) {
        return (
            <div className={'announcement-detail'}>
                {header}
                <Empty className={'announcement-detail-empty'} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
        );
    }

    const { announcement, isAdmin, stats } = state;
    const percent = stats && stats.total > 0 ? Math.round((stats.read / stats.total) * 100) : 0;

    return (
        <div className={'announcement-detail'}>
            {header}

            <div className={'announcement-detail-identity'}>
                <span className={'announcement-detail-avatar'} aria-hidden>
                    <SoundOutlined />
                </span>
                <span className={'announcement-detail-identity-text'}>
                    <span className={'announcement-detail-source'}>{translate({ key: 'announcements.source' })}</span>
                    <span className={'announcement-detail-date'}>
                        {dayjs(announcement.publishedAt).format('DD/MM/YY HH:mm')}
                    </span>
                </span>
            </div>

            <h2 className={'announcement-detail-title'}>{announcement.title}</h2>
            <p className={'announcement-detail-body'}>{announcement.body}</p>

            {announcement.imageUrl && (
                <img className={'announcement-detail-image'} src={announcement.imageUrl} alt={announcement.title} />
            )}

            {isAdmin && stats && (
                <div className={'announcement-detail-reach'}>
                    <div className={'announcement-detail-reach-header'}>
                        <EyeOutlined aria-hidden />
                        <span>{translate({ key: 'announcements.reach-title' })}</span>
                        {stats.total <= 1 && (
                            <Tag className={'announcement-detail-reach-badge'}>
                                {translate({ key: 'announcements.reach-private' })}
                            </Tag>
                        )}
                    </div>

                    {stats.total > 1 && (
                        <>
                            <div className={'announcement-detail-reach-number'}>
                                <span className={'announcement-detail-reach-number-big'}>{stats.read}</span>
                                <span className={'announcement-detail-reach-number-of'}>
                                    {translate({
                                        key: 'announcements.reach-of',
                                        extra: {
                                            total: stats.total,
                                            noun: translate({ key: 'announcements.audience-noun' }),
                                        },
                                    })}
                                </span>
                            </div>
                            <Progress
                                percent={percent}
                                showInfo={false}
                                strokeColor={'#553bbd'}
                                className={'announcement-detail-reach-progress'}
                            />
                            <span className={'announcement-detail-reach-caption'}>
                                {translate({ key: 'announcements.reach-percent', extra: { percent } })}
                            </span>
                        </>
                    )}
                </div>
            )}

            {isAdmin && (
                <button
                    type={'button'}
                    className={'announcement-detail-retire'}
                    aria-label={translate({ key: 'announcements.retire' })}
                    onClick={showConfirmRetire}
                >
                    {translate({ key: 'announcements.retire' })}
                </button>
            )}
        </div>
    );
};
